import { Grid, Typography, Button, Paper, Box } from "@mui/material";
import { FC, useContext, useState, useEffect } from "react";
import ExostarLogo from "src/assets/exostarLogo.png";
import AuthService from "src/services/auth.service";
import RocheLogoBig from "src/assets/rocheLogoBig.png";
import { AuthContext } from "src/utils/authContext";
import { useNavigate } from "react-router-dom";
import FormService from "src/services/form.service"; // Import the FormService for fetching data
import { styled } from "@mui/material/styles"; // Import styled for styling

// Div styled component to match the one in formpage.tsx
const Div = styled("div")(({ theme }) => ({
  ...theme.typography.h6,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  justifyContent: "center",
  alignItems: "baseline",
  padding: theme.spacing(1),
}));

const noDataBackgrougSx = {
  justifyContent: "center",
  padding: "50px",
  display: "flex",
  flexDirection: "column",
  minWidth: "100vh",
  marginTop: "50px", // Ensure Paper doesn't overlap "Last Login"
};

const rocheLogoSx = {
  height: 128,
  width: 256,
  placeSelf: "center",
};

type Props = {
  forEmail?: string;
  hideHistoryButton?: boolean;
  hideLogoutButton?: boolean;
};

const NoData: FC<Props> = ({
  forEmail,
  hideHistoryButton = false,
  hideLogoutButton = false,
}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate(); // Use navigate hook
  const [lastLogin, setLastLogin] = useState<string | null>(null); // State to store the last login date
  const [isLoading, setIsLoading] = useState<boolean>(true); // Loading state

  // Fetch last login data on component mount
  useEffect(() => {
    const fetchLastLogin = async () => {
      try {
        const response = await FormService().getData(
          forEmail || user.emailaddress,
        ); // Fetch data using the same method as FormPage
        setLastLogin(response.lastLogin); // Assuming response has a field `lastLogin`
        setIsLoading(false); // Set loading to false after fetching
      } catch (error) {
        console.error("Error fetching last login:", error);
        setIsLoading(false);
      }
    };

    fetchLastLogin();
  }, [user.emailaddress]); // Re-run if user.emailaddress changes

  const goToHistory = () => {
    navigate("/history"); // Navigate to the /history route
  };

  if (isLoading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ height: "100vh" }}
      >
        <Typography variant="h6">Loading...</Typography>{" "}
        {/* Simple loading text */}
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ position: "relative" }} // Ensuring the parent grid takes up space for positioning
    >
      {/* Last Login label outside Paper container, positioned absolutely */}
      <Grid
        container
        justifyContent="flex-end"
        sx={{ position: "absolute", top: 10, right: 20, zIndex: 10 }}
      >
        <Grid item>
          {/* Use same font styling as in formpage.tsx */}
          <Div>
            Last Login:{" "}
            <Typography variant="body1">
              {lastLogin || "No data available"}
            </Typography>
          </Div>
        </Grid>
      </Grid>

      <Paper variant="elevation" elevation={2} sx={noDataBackgrougSx}>
        <Box
          component="img"
          sx={rocheLogoSx}
          alt="Roche Logo"
          src={RocheLogoBig}
          py={2}
        />
        <Grid item>
          <Typography variant="h4" align="center" gutterBottom>
            No pending actions required
          </Typography>
        </Grid>
        {!hideLogoutButton && (
          <Grid item>
            <Grid container justifyContent="center">
              <Grid item>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  endIcon={
                    <Box component="img" src={ExostarLogo} alt="ExostarLogo" />
                  }
                  size="medium"
                  onClick={() => AuthService().logOut(user.emailaddress)}
                >
                  Logout from
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Add space between the container and History button */}
      <Box sx={{ height: "20px" }} />

      {/* History Button below the container, but above the footer */}
      {!hideHistoryButton && (
        <Grid
          container
          justifyContent="flex-start"
          sx={{ width: "100%", paddingLeft: "60px", paddingBottom: "20px" }}
        >
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={goToHistory}
            style={{
              backgroundColor: "rgb(46, 125, 50)",
              border: "none",
              borderRadius: "5px",
              color: "white",
              fontSize: "12px",
              padding: "10px 10px",
              height: "35px",
            }}
          >
            HISTORY
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default NoData;
